import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { IotAssetsService } from '../../../core/services/iotAssets.service';
import { CatalogService } from '../../../core/services/catalog.service';
import { AssetMappingConfig, AssetFieldConfig, extractNameFromFieldConfigKey } from '../../../core/models/asset-config.model';
import { takeUntil, Subject, Subscription, tap, of, map, catchError, switchMap, Observable } from 'rxjs';
import { AppStateInterface } from '../../../appState.interface';
import { getCompleteState, selectUserFeature, selectedCustomerSelector } from '../../../core/store/selectors';
import { SelectedCustomerCacheInterface } from '../../../core/store/interface/selectedCustomerCache.interface';
import { MAP_FIELD_SCOPES, MapFieldScope } from '../../../core/config/config.constants';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilsService } from '../../../core/services/utils.service';
import { DataService } from '../../../core/services/data.service';
import { WarningModalComponent } from '../warning-modal/warning-modal.component';
import { SaveConfigurationModalComponent, SaveConfigurationModalData } from '../save-configuration-modal/save-configuration-modal.component';
import { KpiJourneySelectorComponent } from '../kpi-journey-selector/kpi-journey-selector.component';
import { SignalsCatalogComponent } from '../signals-catalog/signals-catalog.component';
import { MapConfigurationComponent } from '../map-configuration/map-configuration.component';
import { User } from '../../../core/models/user';
import { faSave, faChevronRight, faChevronDown, faSearch, faTimes as faClear, faQuestionCircle, faWarning, faInfoCircle, faGripVertical, faSignal, faCogs, faDesktop, faBuilding, faWrench, faToggleOn, faComment, faColumns, faListUl, faSort, faMap, faUser, faGlobe, faDatabase, faTrash, faDashboard, faPalette } from '@fortawesome/free-solid-svg-icons';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { cloneDeep } from 'lodash';
import { getSignalsCatalogAction } from 'src/app/core/store/actions';
import { DashboardConfigSelectorComponent } from '../dashboard-config-selector/dashboard-config-selector.component';
import { GraphicConfigComponent } from '../graphic-config/graphic-config.component';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-general-setting',
  templateUrl: './general-setting.component.html',
  styleUrls: ['./general-setting.component.scss']
})
export class GeneralSettingComponent implements OnInit, OnDestroy, OnChanges {
  // Costanti di configurazione
  private static readonly MAX_POPUP_FIELDS = 10;
  private static readonly CUSTOMER_CHANGE_DELAY_MS = 500;
  private static readonly SIDEBAR_UPDATE_DELAY_MS = 100;
  @Input() isFromPortalConfig: boolean = false; // Flag per distinguere se il componente è usato in Portal Config o altrove
  @Input() selectedCustomer: SelectedCustomerCacheInterface | null = null;

  @Output() closePanel = new EventEmitter<void>();

  // Riferimento al componente KPI Journey Selector
  @ViewChild(KpiJourneySelectorComponent) kpiJourneySelector?: KpiJourneySelectorComponent;

  // Riferimento al componente Signals Catalog
  @ViewChild(SignalsCatalogComponent) signalsCatalog?: SignalsCatalogComponent;

  // Riferimento al componente Map Configuration
  @ViewChild(MapConfigurationComponent) mapConfiguration?: MapConfigurationComponent;

  //Riferimento al componente di configurazione dashboard
  @ViewChild(DashboardConfigSelectorComponent) dashboardConfiguration?: DashboardConfigSelectorComponent;

  // Riferimento al componente Graphics Configuration
  @ViewChild(GraphicConfigComponent) graphicsConfiguration?: GraphicConfigComponent;

  // Controllo del ciclo di vita del componente
  private destroy$ = new Subject<void>();

  // Store NgRx per gestire lo state e i cambiamenti di customer
  private storeSubscription: Subscription | null = null;
  private selectedCustomerUid: string | null = null;
  private oldCustomer: SelectedCustomerCacheInterface | null = null;
  private subselectedCustomerSelector: Subscription | null = null;

  // Cliente corrente per il template
  currentCustomer: SelectedCustomerCacheInterface | null = null;

  // Customer da cui stiamo ereditando la configurazione
  selectedCustomerFromStore$: any = null;
  userFeature$: Subscription | null = null; // Per ottenere informazioni sull'utente corrente

  // Utente corrente per il template
  currentUser: User | null = null;

  // Configurazione dei campi mappa
  mappingConfig: AssetMappingConfig | null = null;

  mappingSub$: Subscription | null = null;

  // Segnali filtrati per KPI configuration (scope="signal")
  kpiSignals: Array<{ name: string; label: string; scope: string; unit?: string }> = [];

  // KPI filtrati per KPI Journey Selector (scope="kpi" con visible !== false dal catalog_config)
  // undefined = nessun catalog_config, il componente usa fallback API
  journeyKpiList: Array<{ uid: string; name: string; label: string; scope: string; unit?: string }> | undefined = undefined;

  // Audience per il salvataggio della configurazione
  selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';

  mapFields: { [scope: string]: Array<{
    key: string;
    config: AssetFieldConfig;
    visible: boolean;
  }> } = {};

  // Stati di caricamento
  isLoadingConfig = false;
  configError: string | null = null;

  // Stati accordion per i gruppi
  expandedGroups: { [scope: string]: boolean } = {};

  // Ricerca campi
  searchQuery: string = '';

  // Tab attivo
  activeTab: 'fields' | 'preferences' | 'advanced' = 'fields';

  // Indice del tab selezionato (per nascondere opzioni audience nel tab KPI)
  selectedTabIndex: number = 0;

  // Tab info attivo per il menu info
  activeTabInfo: 'catalog' | 'preferences' | 'fields' | 'kpiJourney' | 'dashboard' | 'graphics' = 'catalog';

  // Filtro per modelli CAN
  selectedModel: string = '';
  availableModels: string[] = [];

  filteredMapFields: { [scope: string]: Array<{
    key: string;
    config: AssetFieldConfig;
    visible: boolean;
    highlighted?: boolean;
  }> } = {};

  // Ordine degli scope (draggable)
  scopeOrder: string[] = [];

  // Icone FontAwesome
  faSave = faSave;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faSearch = faSearch;
  faClear = faClear;
  faQuestionCircle = faQuestionCircle;
  faWarning = faWarning;
  faInfoCircle = faInfoCircle;
  faGripVertical = faGripVertical;
  faSignal = faSignal;
  faCogs = faCogs;
  faDesktop = faDesktop;
  faDashboard = faDashboard;
  faBuilding = faBuilding;
  faWrench = faWrench;
  faToggleOn = faToggleOn;
  faComment = faComment;
  faColumns = faColumns;
  faListUl = faListUl;
  faSort = faSort;
  faMap = faMap;
  faUser = faUser;
  faGlobe = faGlobe;
  faDatabase = faDatabase;
  faTrash = faTrash;
  faPalette = faPalette;

  constructor(
    private iotAssetsService: IotAssetsService,
    private catalogService: CatalogService,
    private store: Store<AppStateInterface>,
    private translateService: TranslateService,
    private logger: LoggerService,
    private dialog: MatDialog,
    public utilsService: UtilsService,
    private dataService: DataService,
    private _spinner: NgxSpinnerService
  ) {
    // Sottoscrizione allo store per il customer selezionato (per ereditarietà configurazione)
    this.subselectedCustomerSelector = this.store.pipe(select(selectedCustomerSelector)).subscribe((res) => {
      this.selectedCustomerFromStore$ = structuredClone(res);

      this.logger.debug('general-setting', 'Customer selezionato dal selector:', {
        customerUid: res?.uid,
        customerName: res?.name
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    /* this.selectedAudience = changes['isFromPortalConfig']?.currentValue ? 'CUSTOMER' : 'USER';
    if (changes['selectedCustomer'] && changes['selectedCustomer'].currentValue) {

      this.manageCustomerChange(changes['selectedCustomer'].currentValue);
    }  */
  }

  ngOnInit(): void {
    // Inizializza il servizio se non è già dato fatto
    this.iotAssetsService.initializeService();

    // Sottoscrizione allo store per gestire cambiamenti di customer
    if (!this.isFromPortalConfig) {
    this.store.dispatch(getSignalsCatalogAction({
      data: {
        customerId: this.selectedCustomerFromStore$?.uid,
      }
    }));
    // 🛡️ Forza selectedAudience = 'USER' per utenti senza permessi admin
    this.enforceUserAudienceForNonAdmins();

    this.storeSubscription = this.store
      .pipe(select(getCompleteState))
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.selectedAudience = 'USER';
        this.manageCustomerChange(state.applyCustomer);
      });
    } else {
      this.store.dispatch(getSignalsCatalogAction({
        data: {
          customerId: this.selectedCustomer?.uid,
        }
      }));
      this.selectedAudience = 'CUSTOMER'; // Forza audience CUSTOMER in Portal Config
      this.manageCustomerChange(this.selectedCustomer!);
    }

    // Sottoscrizione allo store per l'utente corrente
    this.userFeature$ = this.store
      .pipe(select(selectUserFeature))
      .pipe(takeUntil(this.destroy$))
      .subscribe((userInfo) => {
        this.currentUser = userInfo?.user || null;
        this.logger.debug('general-setting', 'Aggiornato utente corrente', {
          user: this.currentUser ? `${this.currentUser.firstName} ${this.currentUser.lastName}` : 'null'
        });
      });

    // Carica la configurazione iniziale
    this.loadMappingConfiguration();
  }

  ngOnDestroy(): void {
    // Pulisci la subscription dello store
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }

    // Pulisci la subscription del customer selector
    if (this.subselectedCustomerSelector) {
      this.subselectedCustomerSelector.unsubscribe();
    }

    // Pulisci la subscription dell'utente
    if (this.userFeature$) {
      this.userFeature$.unsubscribe();
    }

    this.mappingSub$?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  manageCustomerChange(customer: SelectedCustomerCacheInterface): void {
    const currentCustomer = customer;

    this.logger.debug('general-setting', 'Ricevuto cambio state', {
      oldCustomer: this.oldCustomer?.uid,
      newCustomer: currentCustomer?.uid
    });

    // Se è il primo caricamento
    if (!this.oldCustomer) {
      this.oldCustomer = currentCustomer;
      this.currentCustomer = currentCustomer;
      this.selectedCustomerUid = currentCustomer?.uid || null;

      if (this.selectedCustomerUid) {
        this.logger.info('general-setting', `Primo caricamento per customer: ${this.selectedCustomerUid}`);
        this.loadMappingConfiguration();
      }
    }
    // Se c'è stato un cambio di customer
    else if (this.oldCustomer.uid !== currentCustomer?.uid) {
      this.logger.info('general-setting', 'CAMBIO CUSTOMER rilevato', {
        from: this.oldCustomer.uid,
        to: currentCustomer?.uid
      });

      this.oldCustomer = currentCustomer;
      this.currentCustomer = currentCustomer;
      this.selectedCustomerUid = currentCustomer?.uid || null;

      // Reset della configurazione
      this.mappingConfig = null;
      this.mapFields = {};
      this.configError = null;

      // Ricarica la configurazione per il nuovo customer
      if (this.selectedCustomerUid) {
        // Notifica al servizio del cambio customer
        this.iotAssetsService.handleCustomerChange(this.selectedCustomerUid);

        // Ricarica la configurazione specifica per il nuovo customer
        setTimeout(() => {
          this.loadMappingConfiguration();
        }, GeneralSettingComponent.CUSTOMER_CHANGE_DELAY_MS);
      }
    }
  }

  /**
   * Carica la configurazione dei campi mappa dal servizio
   */
  loadMappingConfiguration(): void {
    // Verifica che ci sia un customer selezionato
    if (!this.selectedCustomerUid) {
      this.logger.warn('general-setting', 'Nessun customer selezionato, impossibile caricare configurazione');
      this.configError = 'Nessun cliente selezionato';
      return;
    }

    this.logger.debug('general-setting', `Caricamento configurazione per customer: ${this.selectedCustomerUid}`);

    this.isLoadingConfig = true;
    this.configError = null;

    // Usa il metodo del servizio per ottenere la configurazione attuale
    this.mappingSub$ = this.iotAssetsService.loadMappingConfigForStore(this.selectedAudience)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config: AssetMappingConfig) => {
          this.logger.debug('general-setting', `Configurazione caricata per customer ${this.selectedCustomerUid}`, config);

          // Gestione dell'audience dalla configurazione
          /* if (config.audience) {
            this.logger.debug('general-setting', 'Audience trovata nella configurazione', {
              previousAudience: this.selectedAudience,
              newAudience: config.audience
            });
            this.selectedAudience = config.audience;
          } else {
            this.logger.debug('general-setting', `Nessuna audience nella configurazione, mantengo default: ${this.selectedAudience}`);
          } */

          this.mappingConfig = {...config, audience: this.selectedAudience}; // Assicura che audience sia sempre definita
          this.buildFieldsList();

          // Filtra KPI dal catalog_config (ora asincrono)
          this.filterKpiSignals().subscribe({
            next: () => {
              this.isLoadingConfig = false;
            },
            error: () => {
              this.isLoadingConfig = false;
            }
          });
        },
        error: (error) => {
          this.logger.error('general-setting', `Errore nel caricamento della configurazione per customer ${this.selectedCustomerUid}`, error);
          this.configError = this.translateService.instant('confSignalsPanel.errorLoadingConfig');
          this.isLoadingConfig = false;
        }
      });
  }

  /**
   * Costruisce la lista dei campi per la visualizzazione raggruppati per scope
   */
  private buildFieldsList(): void {
    if (!this.mappingConfig || !this.mappingConfig.fields) {
      this.mapFields = {};
      return;
    }

    // Raggruppa i campi per scope (solo quelli rilevanti per la mappa)
    const fieldsByScope: { [scope: string]: any[] } = {};

    Object.keys(this.mappingConfig.fields).forEach(key => {
      const field = {
        key,
        config: this.mappingConfig!.fields[key],
        visible: this.mappingConfig!.fields[key].visible !== false
      };

      const scope = field.config.scope || 'other';

      // Filtra solo gli scope rilevanti per la mappa
      if (MAP_FIELD_SCOPES.includes(scope as MapFieldScope)) {
        if (!fieldsByScope[scope]) {
          fieldsByScope[scope] = [];
        }
        fieldsByScope[scope].push(field);
      }
    });

    // Ordina i campi all'interno di ogni scope
    Object.keys(fieldsByScope).forEach(scope => {
      fieldsByScope[scope].sort((a, b) => {
        // Se entrambi hanno displayOrder, usa quello
        if (typeof a.config.displayOrder === 'number' && typeof b.config.displayOrder === 'number') {
          return a.config.displayOrder - b.config.displayOrder;
        }

        // Se solo uno ha displayOrder, quello viene prima
        if (typeof a.config.displayOrder === 'number') {
          return -1;
        }
        if (typeof b.config.displayOrder === 'number') {
          return 1;
        }

        // Se nessuno ha displayOrder, ordina alfabeticamente per label
        return a.config.label.localeCompare(b.config.label);
      });

      let configCopy = cloneDeep(this.mappingConfig)!;
      // Aggiorna displayOrder per i campi che non ce l'hanno
      fieldsByScope[scope].forEach((field, index) => {
        if (typeof field.config.displayOrder !== 'number') {
          if (configCopy.fields[field.key]) {
            configCopy.fields[field.key] = { ...configCopy.fields[field.key], displayOrder: index };
          }
        }
      });

      this.mappingConfig = configCopy;
    });

    this.logger.debug('general-setting', 'Campi filtrati per scope mappa', Object.keys(fieldsByScope));

    this.mapFields = fieldsByScope;

    // Estrai tutti i modelli disponibili dai campi
    this.extractAvailableModels();

    // Inizializza gli stati dei gruppi accordion
    this.initializeGroupStates();

    // Inizializza la ricerca (copia iniziale)
    this.updateFilteredFields();

    // Carica l'ordine degli scope salvato
    this.loadScopeOrder();
  }

  /**
   * Filtra i segnali con scope="signal" per il componente KPI configuration
   */
  /**
   * Filtra i segnali KPI dal catalog_config salvato
   * Prende solo segnali con scope='signal' e kpi=true
   * Popola anche journeyKpiList con scope='kpi' e visible!=false
   */
  private filterKpiSignals(): Observable<void> {
    this.logger.debug('general-setting', 'Caricamento segnali KPI dal catalog_config...');

    // Carica catalog_config per l'audience corrente
    return this.iotAssetsService.loadCatalogConfiguration(this.selectedAudience)
      .pipe(
        takeUntil(this.destroy$),
        map(result => {
          const catalogConfig = result.data;

          // Imposta l'audience dal catalog_config salvato
          /* if (result.audience) {
            this.logger.debug('general-setting', 'Audience trovata nel catalog_config', {
              previousAudience: this.selectedAudience,
              newAudience: result.audience
            });
            // this.selectedAudience = result.audience;
          } */

          if (!catalogConfig) {
            this.logger.debug('general-setting', 'Nessun catalog_config trovato');
            // Per kpiSignals: usa mappingConfig come fallback
            this.filterKpiSignalsFromMappingConfig();
            // Per journeyKpiList: lascia undefined, kpi-journey-selector userà il suo fallback API
            this.journeyKpiList = undefined;
            this.logger.info('general-setting', 'journeyKpiList = undefined, kpi-journey-selector userà fallback API');
            return;
          }

          // Filtra segnali con kpi=true E visible!==false (per KPI Configuration)
          // Include signal, kpi e kpi_custom che hanno il flag kpi=true
          this.kpiSignals = Object.entries(catalogConfig)
            .filter(([key, signal]: [string, any]) => {
              return signal.kpi === true && signal.visible !== false;
            })
            .map(([key, signal]: [string, any]) => ({
              name: signal.name || key,
              label: signal.label || signal.name || key,
              scope: signal.scope || 'signal',
              unit: signal.unit || undefined
            }));

          // Filtra KPI con scope='kpi' o 'kpi_custom' E visible !== false (per KPI Journey Selector)
          this.journeyKpiList = Object.entries(catalogConfig)
            .filter(([key, signal]: [string, any]) => {
              const isKpiScope = signal.scope === 'kpi' || signal.scope === 'kpi_custom';
              const isVisible = signal.visible !== false; // true o undefined = visible
              return isKpiScope && isVisible;
            })
            .map(([key, signal]: [string, any]) => ({
              uid: signal.uid || key, // uid necessario per journey selector
              name: signal.name || key,
              label: signal.label || signal.name || key,
              scope: signal.scope || 'kpi',
              unit: signal.unit || undefined
            }));

          this.logger.info('general-setting', `Filtrati ${this.kpiSignals.length} segnali KPI e ${this.journeyKpiList.length} KPI Journey (inclusi kpi_custom) dal catalog_config`);
        }),
        catchError((error) => {
          this.logger.error('general-setting', 'Errore caricamento catalog_config per KPI', error);
          // Per kpiSignals: fallback su mappingConfig
          this.filterKpiSignalsFromMappingConfig();
          // Per journeyKpiList: lascia undefined, kpi-journey-selector userà fallback API
          this.journeyKpiList = undefined;
          return of(void 0);
        })
      );
  }

  /**
   * Fallback: filtra KPI signals da mappingConfig.fields (comportamento legacy)
   * NOTA: Popola solo kpiSignals, journeyKpiList resta undefined per permettere fallback API
   */
  private filterKpiSignalsFromMappingConfig(): void {
    if (!this.mappingConfig || !this.mappingConfig.fields) {
      this.kpiSignals = [];
      // NON toccare journeyKpiList, deve restare undefined
      return;
    }

    this.kpiSignals = Object.keys(this.mappingConfig.fields)
      .filter(key => this.mappingConfig!.fields[key].scope === 'signal')
      .map(key => {
        const config = this.mappingConfig!.fields[key];
        const name = extractNameFromFieldConfigKey(key);
        return {
          name,
          label: config.label || name,
          scope: config.scope || 'signal',
          unit: config.unit || undefined
        };
      });

    // NON popolare journeyKpiList - lascia undefined per fallback API in kpi-journey-selector

    this.logger.debug('general-setting', `Filtrati ${this.kpiSignals.length} segnali da mappingConfig (fallback). journeyKpiList lasciato undefined per fallback API componente.`);
  }

  /**
   * Ricarica tutte le configurazioni dopo un salvataggio
   */
  private reloadAllConfigurations(): Observable<void> {
    this.logger.info('general-setting', 'Ricaricamento di tutte le configurazioni...');

    return new Observable(observer => {
      // 1. Ricarica mapping configuration
      this.iotAssetsService.loadMappingConfigForStore(this.selectedAudience)
        .pipe(
          takeUntil(this.destroy$),
          switchMap((config: AssetMappingConfig) => {
            this.logger.debug('general-setting', 'MappingConfig ricaricato', config);
            this.mappingConfig = config;
            this.buildFieldsList();

            // 2. Ricarica catalog configuration e KPI signals
            return this.filterKpiSignals();
          }),
          tap(() => {
            // 3. Ricarica journey configuration nel componente
            if (this.kpiJourneySelector) {
           //   this.kpiJourneySelector.reloadData();
              this.logger.info('general-setting', 'Journey configuration ricaricato');
            }

            // 3b. Ricarica map configuration nel componente
            if (this.mapConfiguration) {
              this.mapConfiguration.reloadData();
              this.logger.info('general-setting', 'Map configuration ricaricato');
            }

            if (this.dashboardConfiguration) {
            //  this.dashboardConfiguration.reloadData();
              this.logger.info('general-setting', 'Dashboard configuration ricaricato');
            }

            // 4. Invalida cache per forzare reload completo
            if (this.currentCustomer?.uid) {
              this.iotAssetsService.invalidateCatalogCacheForCustomer(this.currentCustomer.uid);
              this.iotAssetsService.invalidateJourneyCacheForCustomer(this.currentCustomer.uid);
              this.logger.debug('general-setting', 'Cache invalidate per customer', this.currentCustomer.uid);
            }


          })
        )
        .subscribe({
          next: () => {
            this.logger.info('general-setting', 'Tutte le configurazioni ricaricate con successo');
            observer.next();
            observer.complete();
            this._spinner.hide();
          },
          error: (error) => {
            this.logger.error('general-setting', 'Errore durante il ricaricamento delle configurazioni', error);
            observer.error(error);
            this._spinner.hide();
          }
        });
    });
  }

  /**
   * Estrae tutti i modelli disponibili dai campi configurati
   */
  private extractAvailableModels(): void {
    const modelsSet = new Set<string>();
    let genericFieldsCount = 0;
    let specificFieldsCount = 0;

    Object.values(this.mapFields).forEach(scopeFields => {
      scopeFields.forEach(field => {
        const config = field.config as any;
        if (config.availableInModels && Array.isArray(config.availableInModels) && config.availableInModels.length > 0) {
          specificFieldsCount++;
          config.availableInModels.forEach((model: string) => {
            if (model && model.trim()) {
              modelsSet.add(model.trim());
            }
          });
        } else {
          genericFieldsCount++;
        }
      });
    });

    this.availableModels = Array.from(modelsSet).sort();
    this.logger.debug('general-setting', `Estratti ${this.availableModels.length} modelli specifici + ${genericFieldsCount} campi generici (totale ${specificFieldsCount + genericFieldsCount} campi):`, {
      models: this.availableModels,
      genericFields: genericFieldsCount,
      specificFields: specificFieldsCount
    });
  }

  /**
   * Cambia la visibilità di un campo
   */
  toggleFieldVisibility(fieldKey: string): void {
    // Trova il campo in tutti gli scope
    for (const scope of Object.keys(this.mapFields)) {
      const field = this.mapFields[scope].find(f => f.key === fieldKey);
      if (field) {
        const wasVisible = field.visible;
        field.visible = !field.visible;

        // Aggiorna anche la configurazione
        if (this.mappingConfig && this.mappingConfig.fields[fieldKey]) {
          this.mappingConfig.fields[fieldKey].visible = field.visible;
        }

        // Gestione accordion per singoli campi
        if (field.visible && !wasVisible) {
          // Se abilitiamo un campo, apriamo l'accordion per mostrarlo
          this.expandedGroups[scope] = true;
          this.logger.trace('general-setting', `Gruppo ${scope} aperto (campo ${fieldKey} abilitato)`);
        } else if (!field.visible && wasVisible) {
          // Se disabilitiamo un campo, controlliamo se ci sono altri campi visibili
          const hasVisibleFields = this.mapFields[scope].some(f => f.visible);
          if (!hasVisibleFields) {
            // Se non ci sono più campi visibili, chiudiamo l'accordion
            this.expandedGroups[scope] = false;
            this.logger.trace('general-setting', `Gruppo ${scope} chiuso (nessun campo visibile)`);
          }
        }

        // Aggiorna i campi filtrati per riflettere le modifiche
        this.updateFilteredFields();

        return;
      }
    }
  }

  /**
   * Controlla se tutti i campi di uno scope sono abilitati
   */
  isScopeEnabled(scope: string): boolean {
    if (!this.mapFields[scope] || this.mapFields[scope].length === 0) {
      return false;
    }

    const visibleCount = this.mapFields[scope].filter(field => field.visible).length;
    return visibleCount === this.mapFields[scope].length;
  }

  /**
   * Controlla se uno scope è in stato indeterminato (alcuni campi abilitati, altri no)
   */
  isScopeIndeterminate(scope: string): boolean {
    if (!this.mapFields[scope] || this.mapFields[scope].length === 0) {
      return false;
    }

    const visibleCount = this.mapFields[scope].filter(field => field.visible).length;
    return visibleCount > 0 && visibleCount < this.mapFields[scope].length;
  }

  /**
   * Abilita/disabilita tutti i campi di uno scope
   */
  onScopeToggleChange(scope: string): void {
    if (!this.mapFields[scope]) {
      return;
    }

    // Determina se abilitare o disabilitare tutti i campi
    const shouldEnable = !this.isScopeEnabled(scope);

    this.logger.debug('general-setting', `${shouldEnable ? 'Abilitazione' : 'Disabilitazione'} di tutti i campi per scope: ${scope}`);

    // Aggiorna tutti i campi dello scope
    this.mapFields[scope].forEach(field => {
      field.visible = shouldEnable;

      // Aggiorna anche la configurazione
      if (this.mappingConfig && this.mappingConfig.fields[field.key]) {
        this.mappingConfig.fields[field.key].visible = shouldEnable;
      }
    });

    // Gestione accordion: se disabilitiamo tutti i campi, chiudiamo l'accordion
    if (!shouldEnable) {
      this.expandedGroups[scope] = false;
      this.logger.trace('general-setting', `Gruppo ${scope} chiuso automaticamente (tutti i campi disabilitati)`);
    } else {
      // Se abilitiamo i campi, apriamo l'accordion per mostrarli
      this.expandedGroups[scope] = true;
      this.logger.trace('general-setting', `Gruppo ${scope} aperto automaticamente (campi abilitati)`);
    }

    // Aggiorna i campi filtrati per riflettere le modifiche
    this.updateFilteredFields();
  }

  /**
   * Ottiene lo stato di una destinazione per un campo
   */
  getFieldDestination(fieldKey: string, destination: 'popup' | 'sidebar' | 'details'): boolean {
    if (!this.mappingConfig || !this.mappingConfig.fields[fieldKey]) {
      return false;
    }

    const config = this.mappingConfig.fields[fieldKey];
    switch (destination) {
      case 'popup':
        return config.showInPopup === true;
      case 'sidebar':
        return config.showInSidebar === true;
      case 'details':
        return config.showInDetails !== false;
      default:
        return false;
    }
  }

  /**
   * Gestisce il click sul checkbox popup con controllo preventivo del limite
   */
  onPopupCheckboxClick(event: any, fieldKey: string): void {
    // Previeni il comportamento di default
    event.preventDefault();
    event.stopPropagation();

    const currentlyEnabled = this.getFieldDestination(fieldKey, 'popup');
    const newEnabled = !currentlyEnabled;

    // Se stiamo tentando di abilitare, controlla il limite
    if (newEnabled) {
      const currentPopupCount = this.getActivePopupCount();
      if (currentPopupCount >= GeneralSettingComponent.MAX_POPUP_FIELDS) {
        this.showWarningModal(`Impossibile abilitare popup per ${fieldKey}: limite massimo di ${GeneralSettingComponent.MAX_POPUP_FIELDS} popup raggiunto`);
        this.logger.warn('general-setting', `Tentativo di abilitare popup per ${fieldKey} rifiutato: limite di ${GeneralSettingComponent.MAX_POPUP_FIELDS} raggiunto`);
        return;
      }
    }

    // Procedi con il toggle normale
    this.toggleFieldDestination(fieldKey, 'popup', newEnabled);
  }

  toggleFieldDestination(fieldKey: string, destination: 'popup' | 'sidebar' | 'details', enabled: boolean): void {
    if (!this.mappingConfig || !this.mappingConfig.fields[fieldKey]) {
      return;
    }

    this.logger.debug('general-setting', `Cambiando destinazione ${destination} per campo ${fieldKey}: ${enabled}`);

    const config = this.mappingConfig.fields[fieldKey];
    switch (destination) {
      case 'popup':
        config.showInPopup = enabled;
        break;
      case 'sidebar':
        config.showInSidebar = enabled;
        break;
      case 'details':
        config.showInDetails = enabled;
        break;
    }
  }

  /**
   * Conta il numero di campi con popup abilitato
   */
  private getActivePopupCount(): number {
    if (!this.mappingConfig || !this.mappingConfig.fields) {
      return 0;
    }

    return Object.values(this.mappingConfig.fields)
      .filter(config => config.visible && config.showInPopup === true)
      .length;
  }

  /**
   * Mostra un modal di warning con messaggio tradotto
   */
  private showWarningModal(messageKey: string): void {
    this.dialog.open(WarningModalComponent, {
      width: '400px',
      data: messageKey
    });
  }

  /**
   * Salva le modifiche alla configurazione
   */
  saveConfiguration(): void {
    // Ottieni il nome della tab corrente
    const tabName = this.getCurrentTabName();

    // Ottieni i nomi per audience
    const userName = this.currentUser
      ? `${this.currentUser.firstName} ${this.currentUser.lastName}`
      : 'Utente';
    const customerName = this.currentCustomer?.name || 'Cliente';
    const currentAudienceName = this.selectedAudience === 'USER' ? userName : customerName;

    // Prepara i dati per la modale
    const modalData: SaveConfigurationModalData = {
      tabName,
      currentAudience: this.selectedAudience as 'USER' | 'CUSTOMER',
      currentAudienceName,
      userName,
      customerName,
      isFromPortalConfig: this.isFromPortalConfig
    };

    // Apri la modale di conferma
    const dialogRef = this.dialog.open(SaveConfigurationModalComponent, {
      width: '550px',
      data: modalData,
      disableClose: true
    });

    // Attendi la risposta dell'utente
    dialogRef.afterClosed().subscribe((selectedAudience: 'USER' | 'CUSTOMER' | null) => {
      // Se l'utente ha annullato, non fare nulla
      if (!selectedAudience) {
        this.logger.info('general-setting', 'Salvataggio annullato dall\'utente');
        return;
      }

      // Procedi con il salvataggio
      this.executeSave();
    });
  }

  deleteConfiguration(): void {
    let tabKey = '';
    switch (this.selectedTabIndex) {
      case 0:
        tabKey = 'catalog_';
        break;
      case 1:
        tabKey = '';
        break;
      case 2:
        tabKey = 'journey_';
        break;
      default:
        tabKey = '';
    }
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: 'confSignalsPanel.deleteConfigurationMessage'
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.catalogService.deleteCatalogConfiguration(tabKey + this.currentCustomer!.uid).subscribe({
          next: () => {

          }
        })
      }
    });
  }

  /**
   * Ottiene il nome della tab corrente
   */
  private getCurrentTabName(): string {
    const tabNames: { [key: number]: string } = {
      0: this.translateService.instant('confSignalsPanel.tabCatalog'),
      1: this.translateService.instant('confSignalsPanel.tabPreferences'),
      2: this.translateService.instant('confSignalsPanel.tabFields'),
      3: this.translateService.instant('confSignalsPanel.tabKpiJourney'),
      4: this.translateService.instant('confSignalsPanel.tabDashboard'),
      5: this.translateService.instant('confSignalsPanel.tabGraphics')
    };
    return tabNames[this.selectedTabIndex] || 'Configurazione';
  }

  /**
   * Esegue il salvataggio effettivo della configurazione
   */
  private executeSave(): void {
    this._spinner.show();

    // Se siamo nella tab Signals Catalog (index 0), salva la configurazione catalog
    if (this.selectedTabIndex === 0) {
      this.saveCatalogConfiguration();
      return;
    }

    // Se siamo nella tab KPI Journey (index 2), salva la configurazione journey
    if (this.selectedTabIndex === 2) {
      this.saveJourneyConfiguration();
      return;
    }

   // Se siamo nella tab Dashboard (index 4), salva la configurazione dashboard
    if (this.selectedTabIndex === 4) {
      this.saveDashboardConfiguration();
      return;
    }

    // Se siamo nella tab Graphics (index 5), salva la configurazione graphics
    if (this.selectedTabIndex === 5) {
      this.saveGraphicsConfiguration();
      return;
    }

    // Altrimenti, salva la configurazione mapping normale (tab Map index 1)
    if (!this.mappingConfig) {
      this.logger.warn('general-setting', 'Nessuna configurazione da salvare');
      return;
    }

    this.logger.info('general-setting', 'Salvataggio configurazione campi mappa...');

    // Aggiorna la configurazione interna del servizio
    this.iotAssetsService.setMappingConfig(this.mappingConfig);

    // Salva tramite API usando il nuovo metodo pubblico con audience selezionato
    this.iotAssetsService.saveMappingConfiguration(this.selectedAudience);

    this.logger.info('general-setting', 'Configurazione mapping salvata');

    // Ricarica tutte le configurazioni
    this.reloadAllConfigurations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.logger.info('general-setting', 'Configurazioni ricaricate dopo salvataggio mapping');

          // Forzare l'aggiornamento della sidebar dopo un breve delay
          setTimeout(() => {
            this.logger.debug('general-setting', 'Forzando aggiornamento sidebar...');
            this.iotAssetsService.forceUpdateSidebar();
          }, GeneralSettingComponent.SIDEBAR_UPDATE_DELAY_MS);

          this.closePanel.emit();

          this._spinner.hide();
        },
        error: (error) => {
          this.logger.error('general-setting', 'Errore ricaricamento configurazioni', error);
          this.closePanel.emit(); // Chiudi comunque
          this._spinner.hide();
        }
      });
  }

  /**
   * Salva la configurazione KPI Journey
   */
  private saveJourneyConfiguration(): void {
    if (!this.kpiJourneySelector) {
      this.logger.warn('general-setting', 'Componente KPI Journey Selector non disponibile');
      return;
    }

    const journeyData = this.kpiJourneySelector.getJourneyConfiguration();
    this.logger.info('general-setting', 'Salvataggio configurazione KPI Journey...', journeyData);

    // Salva tramite API con scope journey_config
    this.iotAssetsService.saveJourneyConfiguration(journeyData, this.selectedAudience)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.logger.info('general-setting', 'Configurazione Journey salvata con successo');
        }),
        switchMap(() => {
          // Ricarica tutte le configurazioni dopo il salvataggio
          return this.reloadAllConfigurations();
        })
      )
      .subscribe({
        next: () => {
          this.logger.info('general-setting', 'Journey salvato e configurazioni ricaricate');
          this.closePanel.emit();
        },
        error: (error) => {
          this.logger.error('general-setting', 'Errore salvataggio configurazione Journey', error);
          this.closePanel.emit();
          this._spinner.hide();
        }
      });
  }

  /**
   * Salva la configurazione Signals Catalog
   */
  private saveCatalogConfiguration(): void {
    if (!this.signalsCatalog) {
      this.logger.warn('general-setting', 'Componente Signals Catalog non disponibile');
      return;
    }

    const catalogData = this.signalsCatalog.getCatalogChanges();

    if (!catalogData) {
      this.logger.info('general-setting', 'Nessuna modifica da salvare per Signals Catalog');
      this.closePanel.emit();
      return;
    }

    this.logger.info('general-setting', 'Salvataggio configurazione Signals Catalog...', catalogData);

    // Salva tramite API con scope catalog_config
    this.catalogService.saveCatalogConfiguration(catalogData, this.selectedCustomer?.uid ?? '', this.selectedAudience)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.logger.info('general-setting', 'Configurazione Catalog salvata con successo');
          // Reset delle modifiche pending nel componente
          this.signalsCatalog?.resetPendingChanges();
        }),
        switchMap(() => {
          // Ricarica tutte le configurazioni dopo il salvataggio
          return this.reloadAllConfigurations();
        })
      )
      .subscribe({
        next: () => {
          this.logger.info('general-setting', 'Catalog salvato e configurazioni ricaricate');
          this.closePanel.emit();
        },
        error: (error) => {
          this.logger.error('general-setting', 'Errore salvataggio configurazione Catalog', error);
          this.closePanel.emit();
          this._spinner.hide();
        }
      });
  }

  saveDashboardConfiguration(): void {
    if (!this.dashboardConfiguration) {
      this.logger.warn('general-setting', 'Componente Dashboard Configuration non disponibile');
      return;
    }
    const dashboardConfig = this.dashboardConfiguration?.getDashboardConfiguration();

    if (!dashboardConfig) {
      this.logger.info('general-setting', 'Nessuna modifica da salvare per Dashboard Configuration');
      this.closePanel.emit();
      return;
    }
    this.logger.info('general-setting', 'Salvataggio configurazione Dashboard...', dashboardConfig);
    this.iotAssetsService.saveDashboardConfiguration(dashboardConfig, this.selectedAudience)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.logger.info('general-setting', 'Configurazione Dashboard salvata con successo');
        }),
        switchMap(() => {
          // Ricarica tutte le configurazioni dopo il salvataggio
          return this.reloadAllConfigurations();
        })
      )
      .subscribe({
        next: () => {
          this.logger.info('general-setting', 'Dashboard salvato e configurazioni ricaricate');
          this.closePanel.emit();
        },
        error: (error) => {
          this.logger.error('general-setting', 'Errore salvataggio configurazione Dashboard', error);
          this.closePanel.emit();
          this._spinner.hide();
        }
      });
  }

  saveGraphicsConfiguration(): void {
    if (!this.graphicsConfiguration) {
      this.logger.warn('general-setting', 'Componente Graphics Configuration non disponibile');
      return;
    }
    const graphicsConfig = this.graphicsConfiguration?.getGraphicsConfiguration();
    if (!graphicsConfig) {
      this.logger.info('general-setting', 'Nessuna modifica da salvare per Graphics Configuration');
      this.closePanel.emit();
      return;
    }

    // Check if the config is for the current logged-in user's customer
    const userInfo = this.dataService.getCurrentUserInfo();
    const currentUserCustomerId = userInfo.customer?.uid;
    const configCustomerId = this.currentCustomer?.uid;
    const shouldApplyImmediately = currentUserCustomerId === configCustomerId;

    this.logger.info('general-setting', 'Salvataggio configurazione Graphics...', {
      config: graphicsConfig,
      currentUserCustomer: currentUserCustomerId,
      configCustomer: configCustomerId,
      applyImmediately: shouldApplyImmediately
    });

    // Il customer selezionato nel pannello va passato esplicitamente: il servizio altrimenti
    // userebbe quello applicato nello store, scrivendo la config sul customer sbagliato.
    this.iotAssetsService.saveGraphicsConfiguration(graphicsConfig, this.selectedAudience, this.selectedCustomerUid)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.logger.info('general-setting', 'Configurazione Graphics salvata con successo');

          // Apply graphics changes immediately ONLY if config is for current user's customer
          if (shouldApplyImmediately) {
            this.logger.info('general-setting', 'Applicazione immediata modifiche grafiche (config per customer corrente)');

            if (graphicsConfig.primaryColor) {
              localStorage.setItem('actia-portal-primary-color', graphicsConfig.primaryColor);
              this.utilsService.applyThemeColorVars(graphicsConfig.primaryColor);
              this.logger.debug('general-setting', 'Primary color applied:', graphicsConfig.primaryColor);
            } else {
              localStorage.removeItem('actia-portal-primary-color');
              // Colore azzerato: si ripristina il colore di brand del tenant, altrimenti le
              // variabili resterebbero sul colore appena cancellato.
              this.utilsService.applyThemeColorVars(this.utilsService.getMainColor());
              this.logger.debug('general-setting', 'Primary color removed, CSS variable reset');
            }

            if (graphicsConfig.logo) {
              localStorage.setItem('actia-portal-logo', graphicsConfig.logo);
              this.logger.debug('general-setting', 'Logo stored in localStorage');

              // Trigger custom event to notify header component
              window.dispatchEvent(new CustomEvent('portal-logo-changed', {
                detail: { logo: graphicsConfig.logo }
              }));
            } else {
              localStorage.removeItem('actia-portal-logo');
              this.logger.debug('general-setting', 'Logo removed from localStorage');

              // Trigger custom event to notify header component
              window.dispatchEvent(new CustomEvent('portal-logo-changed', {
                detail: { logo: null }
              }));
            }

            if (graphicsConfig.portalTitle) {
              document.title = graphicsConfig.portalTitle;
              this.logger.debug('general-setting', 'Portal title applied:', graphicsConfig.portalTitle);
            }
          } else {
            this.logger.info('general-setting', 'Modifiche grafiche NON applicate immediatamente (config per customer diverso)');
          }
        }),
        switchMap(() => {
          // Ricarica tutte le configurazioni dopo il salvataggio
          return this.reloadAllConfigurations();
        })
      )
      .subscribe({
        next: () => {
          this.logger.info('general-setting', 'Graphics salvato e configurazioni ricaricate');
          this.closePanel.emit();

          // Reload the page ONLY if changes were applied to current customer
          if (shouldApplyImmediately) {
            this.logger.info('general-setting', 'Ricaricamento pagina per applicare tutte le modifiche grafiche...');
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        },
        error: (error) => {
          this.logger.error('general-setting', 'Errore salvataggio configurazione Graphics', error);
          this.closePanel.emit();
          this._spinner.hide();
        }
      });
  }

  /**
   * Gestisce il cambio di audience selezionato con validazione permessi
   */
  onAudienceChange(newAudience: 'USER' | 'CUSTOMER' | 'GLOBAL'): void {
    /* this.logger.debug('general-setting', 'Cambio audience richiesto', {
      previousAudience: this.selectedAudience,
      newAudience
    }); */

    // Validazione permessi per utenti normali
    if (!this.utilsService.isUserAdmin() && !this.utilsService.isUserActiaAdmin()) {
      this.logger.warn('general-setting', 'Tentativo cambio audience da utente normale bloccato');
      //this.selectedAudience = 'USER';
      return;
    }

    // Validazione permessi per admin
    // COMMENTATO TEMPORANEAMENTE - per ora salviamo solo per USER e CUSTOMER
    // if (newAudience === 'GLOBAL' && !this.utilsService.isUserActiaAdmin()) {
    //   this.logger.warn('general-setting', 'Tentativo accesso non autorizzato a audience GLOBAL');
    //   this.selectedAudience = 'USER';
    //   return;
    // }

    if (newAudience === 'CUSTOMER' && !this.utilsService.isUserAdmin() && !this.utilsService.isUserActiaAdmin()) {
      this.logger.warn('general-setting', 'Tentativo accesso non autorizzato a audience CUSTOMER');
      //this.selectedAudience = 'USER';
      return;
    }

    // ✅ Validazione superata, aggiorna audience
    //this.selectedAudience = newAudience;
    this.logger.info('general-setting', `Audience cambiata a: ${newAudience}`);

    // Aggiorna anche la configurazione se presente
    if (this.mappingConfig) {
      this.mappingConfig.audience = newAudience;
      this.logger.debug('general-setting', `Audience aggiornata nella configurazione: ${newAudience}`);
    }

    // Ricarica i segnali KPI per la nuova audience
    this.filterKpiSignals().subscribe();
  }

  /**
   * Inizializza gli stati dei gruppi - espande quelli che hanno campi visibili
   */
  private initializeGroupStates(): void {
    Object.keys(this.mapFields).forEach(scope => {
      this.expandedGroups[scope] = false;
    });
  }

  /**
   * Aggiorna i campi filtrati in base alla query di ricerca
   */
  updateFilteredFields(): void {
    this.filteredMapFields = {};

    const hasTextSearch = this.searchQuery && this.searchQuery.trim() !== '';
    const hasModelFilter = this.selectedModel && this.selectedModel.trim() !== '';

    Object.keys(this.mapFields).forEach(scope => {
      let filteredFields = this.mapFields[scope];
      let shouldHighlight = false;

      // Applica filtro per testo
      if (hasTextSearch) {
        const query = this.searchQuery.toLowerCase().trim();
        filteredFields = filteredFields.filter(field => {
          return field.key.toLowerCase().includes(query) ||
                 field.config.label.toLowerCase().includes(query);
        });
        shouldHighlight = true;
      }

      // Applica filtro per modello CAN
      if (hasModelFilter) {
        filteredFields = filteredFields.filter(field => {
          const config = field.config as any;
          if (!config.availableInModels || !Array.isArray(config.availableInModels) || config.availableInModels.length === 0) {
            return true;
          }
          return config.availableInModels.includes(this.selectedModel);
        });
      }

      // Mappa i risultati con highlight
      if (filteredFields.length > 0) {
        this.filteredMapFields[scope] = filteredFields.map(field => ({
          ...field,
          highlighted: shouldHighlight
        }));

        // Espandi automaticamente i gruppi con risultati
        if (hasTextSearch || hasModelFilter) {
          this.expandedGroups[scope] = true;
        }
      }
    });

    this.logger.debug('general-setting', `Ricerca "${this.searchQuery}": trovati ${this.getTotalFilteredFieldsCount()} campi`);
  }

  /**
   * Gestisce il cambio del testo di ricerca
   */
  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.updateFilteredFields();
  }

  /**
   * Pulisce la ricerca
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.updateFilteredFields();
  }

  /**
   * Gestisce il cambio del filtro modello
   */
  onModelFilterChange(model: string): void {
    this.selectedModel = model;
    this.updateFilteredFields();
  }

  /**
   * Gestisce il drop di un campo per riordinamento
   */
  onFieldDrop(event: CdkDragDrop<any[]>, scope: string): void {
    if (event.previousIndex !== event.currentIndex) {
      // Riordina i campi nell'array filtrato
      moveItemInArray(
        this.filteredMapFields[scope],
        event.previousIndex,
        event.currentIndex
      );

      // Riordina anche nell'array originale
      if (this.mapFields[scope]) {
        const draggedField = this.filteredMapFields[scope][event.currentIndex];
        const originalIndex = this.mapFields[scope].findIndex(f => f.key === draggedField.key);

        if (originalIndex !== -1) {
          const [movedField] = this.mapFields[scope].splice(originalIndex, 1);

          let newOriginalIndex = 0;
          for (let i = 0; i < event.currentIndex; i++) {
            const fieldKey = this.filteredMapFields[scope][i].key;
            const index = this.mapFields[scope].findIndex(f => f.key === fieldKey);
            if (index >= newOriginalIndex) {
              newOriginalIndex = index + 1;
            }
          }

          this.mapFields[scope].splice(newOriginalIndex, 0, movedField);
        }
      }

      // Salva l'ordine nella configurazione
      this.updateFieldOrderInConfig(scope);

      this.logger.debug('general-setting', `Campo riordinato in scope ${scope}:`, {
        from: event.previousIndex,
        to: event.currentIndex,
        fieldKey: this.filteredMapFields[scope][event.currentIndex].key
      });
    }
  }

  /**
   * Aggiorna l'ordine dei campi nella configurazione dopo il drag & drop
   */
  private updateFieldOrderInConfig(scope: string): void {
    if (!this.mappingConfig || !this.mapFields[scope]) {
      return;
    }

    this.mapFields[scope].forEach((field, index) => {
      if (this.mappingConfig!.fields[field.key]) {
        this.mappingConfig!.fields[field.key].displayOrder = index;
      }
    });

    this.logger.debug('general-setting', `Ordine aggiornato per scope ${scope}:`,
      this.mapFields[scope].map((f, i) => ({ key: f.key, order: i }))
    );
  }

  /**
   * TrackBy function per ottimizzare il rendering delle liste
   */
  trackByFieldKey(_index: number, field: any): string {
    return field.key;
  }

  /**
   * Ottiene l'icona appropriata per lo scope
   */
  getScopeIcon(scope: string): any {
    const iconMap: { [key: string]: any } = {
      'signal': this.faSignal,
      'tcu': this.faCogs,
      'platform': this.faDesktop,
      'erp': this.faBuilding,
      'common': this.faWrench,
      'model': this.faCogs,
      'other': this.faQuestionCircle,
      'general': this.faQuestionCircle
    };
    return iconMap[scope] || this.faQuestionCircle;
  }

  /**
   * Conta il numero totale di campi filtrati
   */
  getTotalFilteredFieldsCount(): number {
    return Object.values(this.filteredMapFields).reduce((total, fields) => total + fields.length, 0);
  }

  /**
   * Restituisce le chiavi degli scope filtrati
   */
  getFilteredScopeKeys(): string[] {
    return Object.keys(this.filteredMapFields);
  }

  /**
   * Gestisce il drag & drop degli scope per riordinarli
   */
  onScopeDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.scopeOrder, event.previousIndex, event.currentIndex);
      this.saveScopeOrder();

      this.logger.debug('general-setting', `Scope riordinato:`, {
        from: event.previousIndex,
        to: event.currentIndex,
        newOrder: this.scopeOrder
      });
    }
  }

  /**
   * TrackBy function per gli scope
   */
  trackByScope(_index: number, scope: string): string {
    return scope;
  }

  /**
   * Toggle accordion per i gruppi
   */
  toggleGroup(scope: string): void {
    this.expandedGroups[scope] = !this.expandedGroups[scope];
  }

  /**
   * Salva l'ordine degli scope nel localStorage
   */
  private saveScopeOrder(): void {
    try {
      localStorage.setItem('general-setting-scope-order', JSON.stringify(this.scopeOrder));
    } catch (error) {
      this.logger.error('general-setting', 'Errore nel salvataggio ordine scope:', error);
    }
  }

  /**
   * Carica l'ordine degli scope dal localStorage
   */
  private loadScopeOrder(): void {
    try {
      const saved = localStorage.getItem('general-setting-scope-order');
      if (saved) {
        const savedOrder = JSON.parse(saved);
        const validScopes = savedOrder.filter((scope: string) =>
          Object.keys(this.filteredMapFields).includes(scope)
        );

        const allScopes = Object.keys(this.filteredMapFields);
        const newScopes = allScopes.filter(scope => !validScopes.includes(scope));

        this.scopeOrder = [...validScopes, ...newScopes];
      } else {
        this.scopeOrder = Object.keys(this.filteredMapFields);
      }
    } catch (error) {
      this.logger.error('general-setting', 'Errore nel caricamento ordine scope:', error);
      this.scopeOrder = Object.keys(this.filteredMapFields);
    }
  }

  /**
   * Ottiene la label tradotta per un campo, usando il fallback se la traduzione non esiste
   */
  getFieldLabel(field: any): string {
    const translationKey = `catalog.signals.${field.key}`;
    const translatedLabel = this.translateService.instant(translationKey);

    if (translatedLabel && translatedLabel !== translationKey) {
      return translatedLabel;
    }

    return field.config.label;
  }

  /**
   * Controlla se un campo ha informazioni sui modelli disponibili
   */
  hasAvailableModels(field: any): boolean {
    return field.config && 'availableInModels' in field.config &&
           Array.isArray(field.config.availableInModels) &&
           field.config.availableInModels.length > 0;
  }

  /**
   * Formatta la lista dei modelli per il tooltip
   */
  formatModelsTooltip(field: any): string {
    const models = field.config.availableInModels;
    if (!models || models.length === 0) {
      return '';
    }

    const header = this.translateService.instant('confSignalsPanel.modelsTooltipHeader');
    const separator = '━'.repeat(25);
    const modelsList = models.map((model: string, index: number) =>
      `${index + 1}. ${model}`
    ).join('\n');
    const footer = this.translateService.instant('confSignalsPanel.modelsTooltipFooter');

    return `${header}\n${separator}\n\n${modelsList}\n\n${footer}`;
  }

  /**
   * Controlla se tutti i campi di uno scope hanno Sidebar abilitato
   */
  areAllSidebarChecked(scope: string): boolean {
    if (!this.mapFields[scope] || this.mapFields[scope].length === 0) {
      return false;
    }

    return this.mapFields[scope]
      .filter(field => field.visible)
      .every(field => this.getFieldDestination(field.key, 'sidebar'));
  }

  /**
   * Controlla se la colonna Sidebar è in stato indeterminato per uno scope
   */
  isSidebarIndeterminate(scope: string): boolean {
    if (!this.mapFields[scope] || this.mapFields[scope].length === 0) {
      return false;
    }

    const visibleFields = this.mapFields[scope].filter(field => field.visible);
    if (visibleFields.length === 0) {
      return false;
    }

    const checkedCount = visibleFields.filter(field =>
      this.getFieldDestination(field.key, 'sidebar')
    ).length;

    return checkedCount > 0 && checkedCount < visibleFields.length;
  }

  /**
   * Abilita/disabilita Sidebar per tutti i campi visibili di uno scope
   */
  toggleAllSidebar(scope: string, enabled: boolean): void {
    if (!this.mapFields[scope]) {
      return;
    }

    this.mapFields[scope]
      .filter(field => field.visible)
      .forEach(field => {
        this.toggleFieldDestination(field.key, 'sidebar', enabled);
      });

    this.logger.debug('general-setting', `${enabled ? 'Abilitazione' : 'Disabilitazione'} Sidebar per tutti i campi visibili dello scope: ${scope}`);
  }

  /**
   * Controlla se tutti i campi di uno scope hanno Dettagli abilitato
   */
  areAllDetailsChecked(scope: string): boolean {
    if (!this.mapFields[scope] || this.mapFields[scope].length === 0) {
      return false;
    }

    return this.mapFields[scope]
      .filter(field => field.visible)
      .every(field => this.getFieldDestination(field.key, 'details'));
  }

  /**
   * Controlla se la colonna Dettagli è in stato indeterminato per uno scope
   */
  isDetailsIndeterminate(scope: string): boolean {
    if (!this.mapFields[scope] || this.mapFields[scope].length === 0) {
      return false;
    }

    const visibleFields = this.mapFields[scope].filter(field => field.visible);
    if (visibleFields.length === 0) {
      return false;
    }

    const checkedCount = visibleFields.filter(field =>
      this.getFieldDestination(field.key, 'details')
    ).length;

    return checkedCount > 0 && checkedCount < visibleFields.length;
  }

  /**
   * Abilita/disabilita Dettagli per tutti i campi visibili di uno scope
   */
  toggleAllDetails(scope: string, enabled: boolean): void {
    if (!this.mapFields[scope]) {
      return;
    }

    this.mapFields[scope]
      .filter(field => field.visible)
      .forEach(field => {
        this.toggleFieldDestination(field.key, 'details', enabled);
      });

    this.logger.debug('general-setting', `${enabled ? 'Abilitazione' : 'Disabilitazione'} Dettagli per tutti i campi visibili dello scope: ${scope}`);
  }

  /**
   * Forza selectedAudience = 'USER' per utenti senza permessi admin
   */
  private enforceUserAudienceForNonAdmins(): void {
    if (!this.utilsService.isUserAdmin() && !this.utilsService.isUserActiaAdmin()) {
      this.selectedAudience = 'USER';
      this.logger.info('general-setting', 'Utente normale: audience forzata a USER');
    } else {
      this.logger.debug('general-setting', 'Utente admin: mantiene controllo completo delle opzioni audience');
    }
  }

  /**
   * Ottiene il nome del customer da cui stiamo ereditando la configurazione
   */
  getConfigCustomerName(): string | null {
    if (!this.mappingConfig?.idCustomer || !this.selectedCustomerFromStore$) {
      return null;
    }

    return this.selectedCustomerFromStore$.name;
  }

  /**
   * Controlla se stiamo ereditando una configurazione da un altro customer
   */
  isInheritingConfiguration(): boolean {
    return this.getConfigCustomerName() !== null;
  }

  /**
   * Ottiene il numero totale di campi
   */
  getTotalFieldsCount(): number {
    return Object.values(this.mapFields).reduce((total, fields) => total + fields.length, 0);
  }

  /**
   * Ottiene il titolo della configurazione basato sull'audience
   */
  getConfigurationTitle(): string {
    if (this.selectedAudience === 'USER' && this.currentUser) {
      return `${this.currentUser.firstName} ${this.currentUser.lastName}`;
    } else if (this.selectedAudience === 'CUSTOMER' && this.currentCustomer) {
      return this.currentCustomer.name || 'Cliente';
    } else if (this.selectedAudience === 'GLOBAL') {
      return this.translateService.instant('confSignalsPanel.global') || 'Globale';
    }
    return '';
  }

  /**
   * Ottiene l'etichetta tradotta per lo scope
   */
  getScopeLabel(scope: string): string {
    const translationKey = `scopeNames.${scope}`;
    const translatedLabel = this.translateService.instant(translationKey);

    if (translatedLabel && translatedLabel !== translationKey) {
      return translatedLabel;
    }

    return scope.toUpperCase();
  }

  /**
   * Verifica se un gruppo è espanso
   */
  isGroupExpanded(scope: string): boolean {
    return this.expandedGroups[scope] === true;
  }

  /**
   * Cambia il tab attivo
   */
  setActiveTab(tab: 'fields' | 'preferences' | 'advanced'): void {
    this.activeTab = tab;
    this.logger.debug('general-setting', `Tab cambiato a: ${tab}`);
  }

  /**
   * Gestisce il cambio di tab (mat-tab-group)
   * Nel tab KPI (index 1) forza audience a CUSTOMER
   * Nel tab KPI Journey (index 2) ricarica i dati salvati
   * Nel tab Dashboard (index 4) ricarica i dati salvati
   * Nel tab Graphics (index 5) ricarica i dati salvati
   */
  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.logger.debug('general-setting', `Tab index cambiato a: ${index}`);

    // Carica l'audience specifica per ogni tab
    switch (index) {
      case 0: // TAB Signals/Catalog
        this.loadAudienceFromCatalogConfig();
        break;
      case 1: // TAB Map/Mappa
        this.loadAudienceFromMappingConfig();
        break;
      case 2: // TAB Journey/Activity
        this.loadAudienceFromJourneyConfig();
        break;
      case 3: // TAB KPI Configuration
        // Forza sempre CUSTOMER per KPI Configuration
        if (this.selectedAudience !== 'CUSTOMER') {
          this.logger.info('general-setting', 'Tab KPI Configuration: forzando audience a CUSTOMER');
          //this.selectedAudience = 'CUSTOMER';
        }
        break;
      case 4: // TAB Dashboard
        // Ricarica dati salvati per dashboard
        if (this.dashboardConfiguration) {
          this.logger.debug('general-setting', 'Ricaricamento dati dashboard');
         // this.dashboardConfiguration.reloadData();
        }
        break;
      case 5: // TAB Graphics
        // Ricarica dati salvati per graphics
        if (this.graphicsConfiguration) {
          this.logger.debug('general-setting', 'Ricaricamento dati graphics');
          this.graphicsConfiguration.reloadData();
        }
        break;
    }
  }

  /**
   * Imposta quale tab info è attivo per il menu
   */
  setActiveTabInfo(tabName: 'catalog' | 'preferences' | 'fields' | 'kpiJourney' | 'dashboard' | 'graphics'): void {
    this.activeTabInfo = tabName;
  }

  /**
   * Ottiene il titolo del tab info attivo
   */
  getTabInfoTitle(): string {
    switch (this.activeTabInfo) {
      case 'catalog':
        return this.translateService.instant('confSignalsPanel.tabCatalog');
      case 'preferences':
        return this.translateService.instant('confSignalsPanel.tabPreferences');
      case 'fields':
        return this.translateService.instant('confSignalsPanel.tabFields');
      case 'kpiJourney':
        return this.translateService.instant('confSignalsPanel.tabKpiJourney');
      case 'dashboard':
        return this.translateService.instant('confSignalsPanel.tabDashboard');
      case 'graphics':
        return this.translateService.instant('confSignalsPanel.tabGraphics');
      default:
        return '';
    }
  }

  /**
   * Ottiene il messaggio del tab info attivo
   */
  getTabInfoMessage(): string {
    switch (this.activeTabInfo) {
      case 'catalog':
        return this.translateService.instant('confSignalsPanel.tabCatalogInfo');
      case 'preferences':
        return this.translateService.instant('confSignalsPanel.tabPreferencesInfo');
      case 'fields':
        return this.translateService.instant('confSignalsPanel.tabFieldsInfo');
      case 'dashboard':
        return this.translateService.instant('confSignalsPanel.tabDashboardInfo');
      case 'kpiJourney':
        return this.translateService.instant('confSignalsPanel.tabKpiJourneyInfo');
      case 'graphics':
        return this.translateService.instant('confSignalsPanel.tabGraphicsInfo');
      default:
        return '';
    }
  }

  /**
   * Carica l'audience dalla configurazione catalog_config
   */
  private loadAudienceFromCatalogConfig(): void {
    this.iotAssetsService.loadCatalogConfiguration(this.selectedAudience)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result.audience) {
            /* this.logger.debug('general-setting', 'Audience caricata da catalog_config', {
              previousAudience: this.selectedAudience,
              newAudience: result.audience
            }); */
            //this.selectedAudience = result.audience;
          } else {
            this.logger.debug('general-setting', 'Nessuna audience in catalog_config, mantengo corrente');
          }
        },
        error: (error) => {
          this.logger.warn('general-setting', 'Errore caricamento audience da catalog_config', error);
        }
      });
  }

  /**
   * Carica l'audience dalla configurazione mapping_config
   */
  private loadAudienceFromMappingConfig(): void {
    if (this.mappingConfig?.audience) {
      /* this.logger.debug('general-setting', 'Audience caricata da mapping_config', {
        previousAudience: this.selectedAudience,
        newAudience: this.mappingConfig.audience
      });
      this.selectedAudience = this.mappingConfig.audience; */
    } else {
      this.logger.debug('general-setting', 'Nessuna audience in mapping_config, mantengo corrente');
    }
  }

  /**
   * Carica l'audience dalla configurazione journey_config
   */
  private loadAudienceFromJourneyConfig(): void {
    const userInfo = this.dataService.getCurrentUserInfo();
    const uidUser = userInfo.user?.uid;

    if (!this.selectedCustomerUid || !uidUser) {
      this.logger.warn('general-setting', 'Impossibile caricare audience da journey_config: mancano credenziali');
      return;
    }

    this.iotAssetsService.getJourneyConfigFromAPI(this.selectedCustomerUid, uidUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (configs) => {
          if (configs && configs.length > 0) {
            const latestConfig = configs[0];
            if (latestConfig?.audience) {
              /* this.logger.debug('general-setting', 'Audience caricata da journey_config', {
                previousAudience: this.selectedAudience,
                newAudience: latestConfig.audience
              });
              this.selectedAudience = latestConfig.audience; */
            } else {
              this.logger.debug('general-setting', 'Nessuna audience in journey_config, mantengo corrente');
            }
          } else {
            this.logger.debug('general-setting', 'Nessun journey_config trovato, mantengo audience corrente');
          }

          // Ricarica i dati del kpi-journey-selector
          // if (this.kpiJourneySelector) {
          //   setTimeout(() => {
          //     this.kpiJourneySelector?.reloadData();
          //   }, 100);
          // }
        },
        error: (error) => {
          this.logger.warn('general-setting', 'Errore caricamento audience da journey_config', error);
        }
      });
  }

  /**
   * Handler per salvataggio configurazione KPI
   */
  // onKpiConfigurationSaved(): void {
  //   this.logger.info('general-setting', 'Configurazione KPI salvata con successo');
  //   // Ricarica i KPI nel journey selector dopo il salvataggio
  //   if (this.kpiJourneySelector) {
  //     this.kpiJourneySelector.reloadData();
  //   }
  // }
}
